import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import JobCard from '../components/JobCard';

export default function Home() {
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const navigate = useNavigate();

  const userStr = localStorage.getItem('user');
  const user = userStr && userStr !== 'undefined' ? JSON.parse(userStr) : null;
  const isEmployer = user?.role === 'employer';

  // Fetch latest 4 jobs
  useEffect(() => {
    const fetchLatestJobs = async () => {
      try {
        const res = await fetch((process.env.REACT_APP_API || 'http://localhost:5000') + '/api/jobs?limit=4');
        const data = await res.json();
        if (res.ok) {
          setFeaturedJobs(data.data || []);
        }
      } catch (err) {
        console.error("Error fetching latest jobs:", err);
      }
    };
    fetchLatestJobs();
  }, []);

  const categories = [
    { name: 'Technology & Engineering', icon: 'fa-microchip', count: 234 },
    { name: 'Product & Design', icon: 'fa-palette', count: 156 },
    { name: 'Sales, Marketing & Growth', icon: 'fa-chart-line', count: 98 },
    { name: 'Finance & Strategy', icon: 'fa-calculator', count: 45 },
    { name: 'Operations & Supply Chain', icon: 'fa-truck-loading', count: 32 },
    { name: 'Human Capital & Administration', icon: 'fa-users-cog', count: 21 },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-body">

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Shapes */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-light-blue rounded-bl-[100px] -z-10 opacity-50"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-accent/20 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block px-4 py-2 bg-blue-50 text-primary font-bold rounded-full text-sm tracking-wide uppercase">
                Smarter Hiring. Stronger Careers. Accelerated Growth.
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-secondary leading-tight">
                Empowering Careers. <br />
                Enabling Smart <span className="text-primary relative">Hiring
                  <svg className="absolute w-full h-3 -bottom-1 left-0 text-accent opacity-50" viewBox="0 0 100 10" preserveAspectRatio="none">
                    <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" />
                  </svg>
                </span>
              </h1>
              <p className="text-lg text-body leading-relaxed max-w-lg">
                Connecting ambitious professionals with forward-thinking companies through smart HR solutions and modern hiring technology.
              </p>

              <div className="bg-white p-3 rounded-full shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center max-w-xl">
                <div className="flex-1 flex items-center px-4 border-r border-gray-100">
                  <i className="fas fa-search text-gray-400 mr-3"></i>
                  <input
                    type="text"
                    placeholder={isEmployer ? "Search candidates by skill..." : "Job title or keyword"}
                    className="w-full bg-transparent outline-none text-secondary placeholder-gray-400"
                  />
                </div>
                <div className="flex-1 flex items-center px-4 hidden md:flex">
                  <i className="fas fa-map-marker-alt text-gray-400 mr-3"></i>
                  <input
                    type="text"
                    placeholder="Location"
                    className="w-full bg-transparent outline-none text-secondary placeholder-gray-400"
                  />
                </div>
                <button
                  onClick={() => navigate(isEmployer ? '/candidates' : '/jobs')}
                  className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center hover:bg-blue-600 transition-all shadow-button hover:scale-105"
                >
                  <i className="fas fa-search"></i>
                </button>
              </div>

              <div className="pt-4 flex items-center gap-4 text-sm text-body">
                <span className="font-bold text-secondary">Popular:</span>
                <div className="flex flex-wrap gap-2">
                  {['Designer', 'Developer', 'Manager'].map(tag => (
                    <span key={tag} className="underline decoration-gray-300 hover:text-primary cursor-pointer transition-colors">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Image/Illustration */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img
                  src={isEmployer
                    ? "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                    : "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  }
                  alt="Professional"
                  className="rounded-3xl shadow-2xl w-full max-w-md mx-auto object-cover h-[600px]"
                />

                {/* Floating Cards */}
                <div className="absolute top-10 -left-10 bg-white p-4 rounded-2xl shadow-card animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                      <i className="fas fa-check"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{isEmployer ? "Candidates" : "Success Rate"}</p>
                      <p className="font-bold text-secondary">{isEmployer ? "Verified" : "98% Hired"}</p>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-20 -right-5 bg-white p-4 rounded-2xl shadow-card animate-float" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-primary">
                      <i className="fas fa-user-tie"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Expert</p>
                      <p className="font-bold text-secondary">Management</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Circle */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-dashed border-primary/20 rounded-full -z-10 animate-spin-slow"></div>
            </div>

          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">Strategic Career Domains</h2>
            <p className="text-body max-w-2xl mx-auto">Discover opportunities across high-growth industries and core business functions.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <div key={cat.name} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-card-hover hover:border-primary/20 transition-all duration-300 group cursor-pointer text-center hover:-translate-y-1">
                <div className="w-16 h-16 mx-auto bg-blue-50 rounded-full flex items-center justify-center text-primary text-2xl mb-6 group-hover:bg-primary group-hover:text-white transition-colors">
                  <i className={`fas ${cat.icon}`}></i>
                </div>
                <h3 className="font-bold text-secondary text-lg mb-2 group-hover:text-primary transition-colors">{cat.name}</h3>
                <p className="text-sm text-gray-400">{cat.count}+ {isEmployer ? "Candidates" : "Jobs"}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Jobs Section - Only for Job Seekers */}
      {!isEmployer && (
        <section className="py-24 bg-light-blue/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">Curated Opportunities</h2>
                <p className="text-body max-w-2xl">Carefully selected roles from high-growth companies across industries.</p>
              </div>
              <Link to="/jobs" className="px-6 py-3 border border-gray-200 rounded-full text-secondary font-bold hover:bg-white hover:shadow-sm transition-all">
                Explore All Opportunities <i className="fas fa-arrow-right ml-2"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredJobs.length > 0 ? (
                featuredJobs.map((job) => (
                  <JobCard key={job._id || job.id} job={job} />
                ))
              ) : (
                <div className="col-span-2 text-center py-12 bg-white rounded-3xl border border-gray-100">
                  <p className="text-body">No opportunities found at the moment. Please check back later.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Discover Section (Blogs) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-4">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4">Discover More</h2>
              <p className="text-body max-w-2xl">Career advice, industry insights, and the latest from Konnectt.</p>
            </div>
            <Link to="/discover" className="px-6 py-3 border border-gray-200 rounded-full text-secondary font-bold hover:bg-white hover:shadow-sm transition-all">
              View All Articles <i className="fas fa-arrow-right ml-2"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                id: 1,
                title: 'Navigating the Future of Remote Work',
                category: 'Career Advice',
                image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80',
                date: 'March 15, 2024'
              },
              {
                id: 2,
                title: 'Top Skills for Tech Recruitment in 2024',
                category: 'Hiring Trends',
                image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80',
                date: 'March 12, 2024'
              },
              {
                id: 3,
                title: 'Building Inclusive Workplace Cultures',
                category: 'Management',
                image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80',
                date: 'March 10, 2024'
              }
            ].map((blog) => (
              <Link key={blog.id} to={`/discover/${blog.id}`} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-3xl mb-6">
                  <img src={blog.image} alt={blog.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold text-primary">
                    {blog.category}
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-2">{blog.date}</p>
                <h3 className="text-xl font-bold text-dark group-hover:text-primary transition-colors leading-tight">
                  {blog.title}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-light-blue relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-primary/5 skew-x-12 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-dark mb-6">Let’s Build What’s Next.</h2>
          <p className="text-body text-lg mb-10 max-w-2xl mx-auto">
            Connecting ambitious professionals with forward-thinking organizations through strategic hiring and human capital solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!user && (
              <Link to="/register" className="px-10 py-4 bg-primary text-white font-bold rounded-full hover:bg-blue-600 transition-all shadow-lg shadow-primary/30 hover:-translate-y-1">
                Explore Opportunities
              </Link>
            )}
            <Link to="/contact" className="px-10 py-4 bg-white text-dark border border-gray-200 font-bold rounded-full hover:bg-gray-50 transition-all hover:-translate-y-1">
              Partner With Konnectt
            </Link>
          </div>
        </div>
      </section>

    </div >
  );
}
